import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import batchLoadProducts from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import cleanupUploadData from "@insite/client-framework/Store/Components/OrderUpload/Handlers/CleanupUploadData";
import processFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/ProcessFile";
import setAllowCancel from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetAllowCancel";
import setErrorsModalIsOpen from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetErrorsModalIsOpen";
import setIsBadFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsBadFile";
import setIsUploading from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsUploading";
import setUploadCancelled from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadCancelled";
import setUploadLimitExceeded from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadLimitExceeded";
import translate from "@insite/client-framework/Translate";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { comingSoonDiscontinueDates } from "@tKgpco/Common/commonFunctions";
import { secondaryButton } from "@tKgpco/Extensions/Widgets/MyLists/MyListsCreateListButtonExtension";
import { changeCustomerSelectCustomerContainerStyles as buttonStyle } from "@tKgpco/Overrides/Widgets/ChangeCustomer/ChangeCustomerSelectCustomerContainer";
import uploadItemTrackingHandler from "@tKgpco/Store/Data/ItemTracking/Handlers/UploadItemTracking";
import uploadComingSoonProduct from "@tKgpco/Store/Data/UploadComingSoonProduct/Handler/UploadComingSoonProductHandler";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { FC, useContext, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { ItemTrackings } from "@tKgpco/Services/ItemTrackingService";
import ItemTrackingSuccessMessage from "@tKgpco/Components/ItemTracking/ItemTrackingSuccessMessage";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Zone from "@insite/client-framework/Components/Zone";

export interface OwnProps {
    descriptionText: React.ReactNode;
    checkInventory: boolean;
    templateUrl: string;
    onUploadProducts: (products: ProductDto[], onSuccess?: () => void) => void;
    extendedStyles?: ItemTrackingFileStyles;
    id: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    uploadedItems: state.components.orderUpload.uploadedItems,
    isUploading: state.components.orderUpload.isUploading,
    products: state.components.orderUpload.products,
    rowErrors: state.components.orderUpload.rowErrors,
    isBadFile: state.components.orderUpload.isBadFile,
    failedToLoadInventory: state.components.orderUpload.failedToLoadInventory,
    productsProcessed: state.components.orderUpload.productsProcessed,
    allowCancel: state.components.orderUpload.allowCancel,
    uploadCancelled: state.components.orderUpload.uploadCancelled,
    uploadLimitExceeded: state.components.orderUpload.uploadLimitExceeded,
    continueUpload: state.components.orderUpload.continueUpload,
});

const mapDispatchToProps = {
    processFile,
    batchLoadProducts,
    cleanupUploadData,
    setAllowCancel,
    setIsBadFile,
    setIsUploading,
    setUploadCancelled,
    setUploadLimitExceeded,
    setErrorsModalIsOpen,
    uploadComingSoonProduct,
    uploadItemTrackingHandler,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ItemTrackingFileStyles {
    mainWrapper?: InjectableCss;
    orderCancellationSuccessfulText?: TypographyPresentationProps;
    uploadLinkTooltipText?: TypographyPresentationProps;
    downloadTemplateButton?: ButtonPresentationProps;
    fileUploader?: FileUploadPresentationProps;
    includeFirstRowCheckbox?: CheckboxProps;
    cancelUploadButton?: ButtonPresentationProps;
    uploadFileButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    uploadingButtonLabel?: TypographyPresentationProps;
    uploadFileButtonLabel?: TypographyPresentationProps;
    downloadTemplateWrap?: InjectableCss;
    buttonWrapper?: InjectableCss;
    backToUplaodButton?: InjectableCss;
}

export const itemTrackingFileStyles: ItemTrackingFileStyles = {
    mainWrapper: {
        css: css`
            width: 100%;
        `,
    },
    orderCancellationSuccessfulText: {
        color: "warning",
        css: css`
            line-height: 25px;
        `,
    },
    uploadLinkTooltipText: {
        css: css`
            margin-bottom: 15px;
        `,
    },
    downloadTemplateButton: {
        css: css`
            margin-bottom: 15px;
        `,
        variant: "tertiary",
    },
    includeFirstRowCheckbox: {
        css: css`
            margin: 15px 0;
            display: block;
            span[role="checkbox"][aria-checked="true"]:not([aria-disabled="true"]) {
                background: #fff;
                border: 1px solid #bebdbd;
                color: #19a3e0;
            }
            span[role="checkbox"]:focus {
                outline: none;
                box-shadow: 0 0 0 1px #e2e3e6;
            }
        `,
    },
    fileUploader: {
        buttonProps: {
            // variant: "tertiary",
            typographyProps: {
                size: TYPOGRAPHY.size.smaller,
                weight: TYPOGRAPHY.weight.bold,
            },
            css: css`
                margin-right: 10px;
                text-transform: uppercase;
                border-radius: 22px;
                border: 1px solid ${COLORS.secondaryBtn};
                min-width: 120px;
                background: ${COLORS.white};
                color: ${COLORS.secondaryBtn};
                & :hover {
                    background: ${COLORS.secondaryBg};
                    border: 1px solid ${COLORS.secondaryBtn};
                }
                &:disabled:hover {
                    background: ${COLORS.disabled.base};
                    border: 1px solid ${COLORS.disabled.base};
                }
            `,
        },
    },
    cancelUploadButton: {
        variant: "secondary",
        css: css`
            float: right;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    uploadFileButton: {
        typographyProps: {
            size: TYPOGRAPHY.size.smaller,
            weight: TYPOGRAPHY.weight.bold,
        },
        css: css`
            text-transform: uppercase;
            border-radius: 22px;
            background-image: linear-gradient(to right, #44a8f7, ${COLORS.link.base});
            border: none;
            min-width: 120px;
            color: ${COLORS.white};
            & :hover {
                background: ${COLORS.secondaryBtn};
            }
            &:disabled:hover {
                background: ${COLORS.disabled.base};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 10px;
                            width: 100%;
                        `,
                        css`
                            margin-left: 15px;
                            width: initial;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    spinner: {
        size: 22,
        css: css`
            margin-right: 8px;
        `,
    },
    uploadingButtonLabel: {
        css: css`
            font-size: 14px;
            font-weight: bold;
            vertical-align: super;
        `,
    },
    uploadFileButtonLabel: {
        css: css`
            font-size: 14px;
            font-weight: bold;
        `,
    },
    downloadTemplateWrap: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            justify-content: right;
            margin-top: 25px;
        `,
    },
    backToUplaodButton: {
        css: css`
            margin-top: 15px;
            background: #edf4f8;
            border: 1px solid #0d70b7;
            border-radius: 22px;
            text-transform: uppercase;
            padding: 0px;
            span {
                padding: 11px 21px;
                color: #0d70b7;
                & :hover {
                    color: #ffffff !important;
                }
            }
        `,
    },
};

const styles = itemTrackingFileStyles;

interface responseInterface {
    dateImported?: string;
    filePathForErrorFix?: string;
    rowsImported?: number;
    rowsWithError?: number;
    totalCount?: number;
    uploadComplete?: string;
    isItemTrackingWorksheet?: boolean;
}

const ItemTrackingFile: FC<Props> = ({
    descriptionText,
    checkInventory,
    templateUrl,
    uploadedItems,
    isUploading,
    products,
    rowErrors,
    isBadFile,
    failedToLoadInventory,
    productsProcessed,
    allowCancel,
    uploadCancelled,
    uploadLimitExceeded,
    continueUpload,
    processFile,
    onUploadProducts,
    batchLoadProducts,
    cleanupUploadData,
    setAllowCancel,
    setIsBadFile,
    setIsUploading,
    setUploadCancelled,
    setErrorsModalIsOpen,
    uploadComingSoonProduct,
    uploadItemTrackingHandler,
    id,
}) => {
    const toasterContext = useContext(ToasterContext);
    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    const [file, setFile] = useState<any>(null);
    const [incorrectFileExtension, setIncorrectFileExtension] = React.useState(false);
    const [maxLimitExceeded, setMaxLimitExceeded] = React.useState(false);
    const [firstRowHeading, setFirstRowHeading] = React.useState(true);

    // State for handling success and failure scenarios.

    const [isSuccess, setIsSuccess] = React.useState(false);
    const [responseData, setResponseData] = React.useState<responseInterface>({});
    const [isLoading, setIsLoading] = React.useState(false);

    const firstRowHeadingChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setFirstRowHeading(value);
    };

    React.useEffect(() => batchGetProducts(), [uploadedItems]);

    React.useEffect(() => checkCompletion(), [productsProcessed]);

    React.useEffect(() => {
        if (isBadFile || uploadLimitExceeded || failedToLoadInventory) {
            setErrorsModalIsOpen({ errorsModalIsOpen: true });
        }
        if (failedToLoadInventory) {
            cancelUploadingHandler();
        }
    }, [isBadFile, uploadLimitExceeded, failedToLoadInventory]);

    const downloadTemplateHandler = () => {
        window.open(`${window.location.origin}${templateUrl}`);
    };

    const clearData = () => {
        cleanupUploadData();
        setFile(null);
        if (fileUploadRef.current?.value) {
            fileUploadRef.current.value = "";
        }
        setFirstRowHeading(false);
    };
    const cancelUploadingHandler = () => {
        setUploadCancelled({ uploadCancelled: true });
        setIsUploading({ isUploading: false });
        cleanupUploadData();
        setFile(null);
        if (fileUploadRef.current?.value) {
            fileUploadRef.current.value = "";
        }
        setFirstRowHeading(false);
    };

    const uploadFileHandler = () => {
        try {
            if (isUploading) {
                return;
            }
            setIsLoading(true);

            setUploadCancelled({ uploadCancelled: false });
            if (!file) {
                return;
            }

            if (file.size > maxSize) {
                setMaxLimitExceeded(true);
                return;
            }

            uploadItemTrackingHandler({
                file,
                onSuccess: (response: any) => {
                    setIsLoading(false);
                    setResponseData(response.rowsImported ? response : response.errorJson);
                    setIsSuccess(true);
                    clearData();
                },
            });
        } catch (err) {
            setIsLoading(false);
            toasterContext.addToast({
                body: err,
                messageType: "danger",
            });
        }
    };

    const onReaderLoad = (fileExtension: string) => {
        return (e: Event) => {
            const data = (e.target as any).result;
            // processFile({ data, fileExtension, firstRowHeading });
        };
    };

    const batchGetProducts = () => {
        cleanupUploadData();
        if (!file) {
            return;
        }

        if (uploadedItems.length === 0) {
            setIsBadFile({ isBadFile: true });
            return;
        }

        const extendedNames = uploadedItems.map(item => item.name);
        batchLoadProducts({ extendedNames, firstRowHeading, checkInventory });
    };

    const checkCompletion = React.useCallback(() => {
        if (!productsProcessed || uploadCancelled) {
            return;
        }
        const comingSoonProduct = products.filter(product => {
            const { isComingSoon } = comingSoonDiscontinueDates(product);
            if (isComingSoon) {
                return true;
            }
            return false;
        });

        uploadComingSoonProduct({ products: uploadedItems });
        if (uploadedItems.length === products.length && rowErrors.length === 0 && comingSoonProduct.length === 0) {
            uploadProducts();
        } else {
            setIsUploading({ isUploading: false });
            setErrorsModalIsOpen({ errorsModalIsOpen: true });
        }
    }, [productsProcessed, uploadCancelled, uploadedItems, products, rowErrors]);

    const uploadProducts = () => {
        setAllowCancel({ allowCancel: false });
        const filteredProducts = products.filter(product => {
            const { isComingSoon } = comingSoonDiscontinueDates(product);
            if (isComingSoon) {
                return false;
            }
            return true;
        });
        onUploadProducts(filteredProducts, () => {
            onUploadingSuccess();
        });
        // if (filteredProducts.length > 0) {
        //     onUploadingSuccess();
        // }
    };

    const onUploadingSuccess = React.useCallback(() => {
        setIsUploading({ isUploading: false });
        cleanupUploadData();
        const filteredProducts = products.filter(product => {
            const { isComingSoon } = comingSoonDiscontinueDates(product);
            if (isComingSoon) {
                return false;
            }
            return true;
        });
        toasterContext.addToast({
            body: `${filteredProducts.length} ${translate("items uploaded")}`,
            messageType: "success",
        });
    }, [products]);

    React.useEffect(() => {
        if (continueUpload) {
            uploadProducts();
        }
    }, [continueUpload]);

    const fileChangeHandler = (event: React.ChangeEvent<any>) => {
        const uploadedFile = event.target.files[0];
        setFile(uploadedFile);
        if (uploadedFile) {
            const fileExtension = getFileExtension(uploadedFile.name);
            const incorrectFileExtension = ["xls", "xlsx", "csv"].indexOf(fileExtension) === -1;
            setIncorrectFileExtension(incorrectFileExtension);
            setIsBadFile({ isBadFile: incorrectFileExtension });
            setUploadLimitExceeded({ uploadLimitExceeded: false });
            if (uploadedFile.size > maxSize) {
                setMaxLimitExceeded(true);
                setIsBadFile({ isBadFile: true });
            } else {
                setMaxLimitExceeded(false);
                setIsBadFile({ isBadFile: false });
            }
        }
    };

    return (
        <GridContainer>
            {isSuccess ? (
                <GridItem width={[12, 12, 6, 6, 6]} style={{ flexDirection: "column", margin: "auto" }}>
                    <ItemTrackingSuccessMessage response={responseData} />
                    <StyledWrapper style={{ textAlign: "right", padding: "0px 15px", width: "100%" }}>
                        <Button
                            {...styles.backToUplaodButton}
                            onClick={() => {
                                setIsSuccess(false);
                            }}
                        >
                            {translate("Back to Upload")}
                        </Button>
                    </StyledWrapper>
                </GridItem>
            ) : (
                <>
                    <GridItem width={12}>
                        <Zone contentId={id} zoneName="Content0" />
                    </GridItem>
                    <GridItem width={12}>
                        <GridContainer>
                            <GridItem width={[12, 12, 6, 6, 6]} style={{ flexDirection: "column" }}>
                                <Zone contentId={id} zoneName="Content2" />
                                <StyledWrapper {...styles.mainWrapper}>
                                    {uploadCancelled && (
                                        <Typography
                                            as="p"
                                            {...styles.orderCancellationSuccessfulText}
                                            data-test-selector="OrderUpload_CancellationSuccessful"
                                        >
                                            {siteMessage("OrderUpload_CancellationSuccessful")}
                                        </Typography>
                                    )}
                                    <Typography as="p" {...styles.uploadLinkTooltipText}>
                                        {descriptionText}
                                    </Typography>
                                    <StyledWrapper {...styles.downloadTemplateWrap}>
                                        <Button
                                            // {...styles.downloadTemplateButton}
                                            {...secondaryButton.createListButton}
                                            onClick={downloadTemplateHandler}
                                        >
                                            {translate("Download Template")}
                                        </Button>
                                    </StyledWrapper>
                                    <FileUpload
                                        {...styles.fileUploader}
                                        inputRef={fileUploadRef}
                                        fileName={file?.name || ""}
                                        accept=".xls,.xlsx,.csv"
                                        label={translate("Upload a File")}
                                        labelPosition="top"
                                        error={
                                            incorrectFileExtension
                                                ? translate("File must be .xls, .xlsx")
                                                : maxLimitExceeded
                                                  ? translate("File size must be less than 5MB")
                                                  : undefined
                                        }
                                        onFileChange={fileChangeHandler}
                                        uid="orderUploadFileUpload"
                                    />
                                    {/* <Checkbox
                                        {...styles.includeFirstRowCheckbox}
                                        onChange={firstRowHeadingChangeHandler}
                                        checked={firstRowHeading}
                                        uid="includeHeading"
                                        data-test-selector="orderUpload_includeHeading"
                                    >
                                        {translate("Include First Row Column Heading")}
                                    </Checkbox> */}
                                    <StyledWrapper {...styles.buttonWrapper}>
                                        <Button
                                            // {...styles.cancelUploadButton}
                                            {...buttonStyle.cancelButton}
                                            onClick={cancelUploadingHandler}
                                            data-test-selector="orderUpload_CancelUpload"
                                            disabled={!isUploading || !allowCancel || uploadCancelled}
                                        >
                                            {translate("Cancel Upload")}
                                        </Button>
                                        <Button
                                            // {...styles.uploadFileButton}
                                            {...buttonStyle.continueButton}
                                            disabled={
                                                !file ||
                                                isUploading ||
                                                isLoading ||
                                                incorrectFileExtension ||
                                                maxLimitExceeded
                                            }
                                            onClick={uploadFileHandler}
                                            data-test-selector="orderUpload_UploadFile"
                                        >
                                            {isLoading ? (
                                                <>
                                                    <LoadingSpinner {...styles.spinner} />
                                                    <Typography {...styles.uploadingButtonLabel}>
                                                        {translate("Uploading...")}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <Typography {...styles.uploadFileButtonLabel}>
                                                    {translate("Upload File")}
                                                </Typography>
                                            )}
                                        </Button>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem width={[12, 12, 6, 6, 6]}>
                                <Zone contentId={id} zoneName="Content1" />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ItemTrackingFile);
